import React from 'react';
import { TextVariant } from '@naf/text';
import { Button } from '@naf-simba/elements-buttons';
import { FrontPageType } from '../../../../../../types/frontPageType';
import BlockContent from '../../../../components/block-content/BlockContent';
import { useDocumentUrlWithNode } from '../../../../hooks/Documents/useDocumentUrl';
import MemoizedImageOrVideo from '../MemoizedImageOrVideo/MemoizedImageOrVideo';
import { useTrackCTA } from '../../../../utils/Tracking/CtaClick/TrackCtaClick';
import * as S from './Hero.styles';
import { useTrackPromotion } from '../../../../hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '../../../../utils/Tracking/Promotion/getPromotionEventTrackingData';

interface Props {
  hero?: FrontPageType['hero'];
}

export const Hero = ({ hero }: Props) => {
  const primaryCtaUrl = useDocumentUrlWithNode(hero?.ctaButton?.data);
  const secondaryCtaUrl = useDocumentUrlWithNode(hero?.secondaryCtaButton?.data);
  const trackCTA = useTrackCTA();
  const primaryPromotion = getPromotionEventTrackingData(hero?.ctaButton?.buttonText, primaryCtaUrl);
  const secondaryPromotion = getPromotionEventTrackingData(hero?.secondaryCtaButton?.buttonText, secondaryCtaUrl);
  const shouldTrackPrimaryPromotion = Boolean(primaryPromotion);
  const shouldTrackSecondaryPromotion = Boolean(secondaryPromotion);
  const { wrapperRef: primaryPromotionRef, trackSelectPromotion: trackPrimaryPromotion } = useTrackPromotion({
    creative_slot: 'frontpage-hero',
    creative_name: `CTA | contained-signature | ${hero?.ctaButton?.buttonText}`,
    promotion_id: primaryPromotion?.promotion_id,
    promotion_name: primaryPromotion?.promotion_name,
  });
  const { wrapperRef: secondaryPromotionRef, trackSelectPromotion: trackSecondaryPromotion } = useTrackPromotion({
    creative_slot: 'frontpage-hero',
    creative_name: `CTA | outlined-signature | ${hero?.secondaryCtaButton?.buttonText}`,
    promotion_id: secondaryPromotion?.promotion_id,
    promotion_name: secondaryPromotion?.promotion_name,
  });

  const handleTracking = (text: string, path: string) => {
    trackCTA({
      cta_position: 'hero-banner',
      text,
      url: path,
    });
  };

  if (!hero) {
    return null;
  }

  return (
    <S.HeroContainer id="hero" role="region" aria-labelledby="hero-header">
      <MemoizedImageOrVideo
        imageId={hero.image?.publicId}
        videoId={hero.video?.publicId}
        altText={hero.image?.altText}
      />
      <S.ContentContainer>
        <S.HeroContent>
          <S.Heading id="hero-header" variant={TextVariant.Display}>
            {hero.header}
          </S.Heading>
          <BlockContent value={hero.body} />
          <S.ButtonWrapper>
            {hero.ctaButton ? (
              <div ref={shouldTrackPrimaryPromotion ? primaryPromotionRef : undefined}>
                <Button
                  size="medium"
                  color="primary"
                  variant="contained"
                  to={primaryCtaUrl}
                  onClick={() => {
                    if (shouldTrackPrimaryPromotion) {
                      trackPrimaryPromotion();
                    }
                    if (primaryCtaUrl) {
                      handleTracking(hero.ctaButton.buttonText, primaryCtaUrl);
                    }
                  }}
                >
                  {hero.ctaButton.buttonText}
                </Button>
              </div>
            ) : null}
            {hero.secondaryCtaButton ? (
              <div ref={shouldTrackSecondaryPromotion ? secondaryPromotionRef : undefined}>
                <Button
                  size="medium"
                  color="primary"
                  variant="outlined"
                  to={secondaryCtaUrl}
                  onClick={() => {
                    if (shouldTrackSecondaryPromotion) {
                      trackSecondaryPromotion();
                    }
                    if (secondaryCtaUrl) {
                      handleTracking(hero.secondaryCtaButton.buttonText, secondaryCtaUrl);
                    }
                  }}
                >
                  {hero.secondaryCtaButton.buttonText}
                </Button>
              </div>
            ) : null}
          </S.ButtonWrapper>
        </S.HeroContent>
      </S.ContentContainer>
    </S.HeroContainer>
  );
};
